import React, { useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import kebabCase from 'lodash/kebabCase';
import classNames from 'classnames';
import { AnalyticsEventSource } from '@atlassian/temp-analytics';
import Button from '@atlaskit/button';
import ExportIcon from '@atlaskit/icon/glyph/export';
import { AppContent, AppPage, useFeatureToggle } from '@css-xp/base';
import { PageBackground, PageBackgroundV2 } from '@css-xp/page-background';
import { Background } from '@css-xp/page-background/src/background-image';
import { InvoiceTable, InvoiceTableColumns } from '@css-xp/ppc-invoice-table';
import { ScreenEventAnalytics } from '@css-xp/ppc-common';
import { DarkLoadingButton } from '@css-xp/ppc-dark-button';
import { InvoicesFilterOptions, SortType } from '@css-xp/graphtypes/src/generated-types-new';
import { useTitle } from '@css-xp/ppc-custom-hooks';
import {
  PPC_PAGE_TITLE,
  ENABLE_DESIGN_ITERATION_1,
  routePaths,
  openAndPaidTabName,
  refundedTabName,
  openAndPaidTabNameKey,
  refundedTabNameKey,
  STATSIG_FEATURE_GATES,
} from '@css-xp/ppc-constants';
import { INVOICES } from './constants/invoices';
import { useInvoicesListCSV } from './hooks/use-invoices-list-csv';
import { ButtonTabContainer } from './invoices-page.styles';
import './invoices-page-v2.scss';
//need to be removed once M2.0 Feature flag is removed
import './invoices-page.scss';

export const InvoicesPage = () => {
  const [selectedTabKey, setSelectedTabKey] = useState<string>(
    location.hash === `#${refundedTabNameKey}` ? refundedTabNameKey : openAndPaidTabNameKey
  );
  const history = useHistory();
  const { txaId } = useParams<{ txaId: string }>();
  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const [downloadCsvOptions, setDownloadCsvOptions] = useState<{
    filterOptions?: InvoicesFilterOptions;
    selectedTab?: string;
    sortOptions?: SortType;
  }>();

  const { downloadInvoicesListCSV, invoicesListLoading } = useInvoicesListCSV(
    txaId,
    downloadCsvOptions?.filterOptions,
    downloadCsvOptions?.selectedTab,
    downloadCsvOptions?.sortOptions
  );

  useTitle(PPC_PAGE_TITLE.INVOICES);

  const renderActions = () => {
    return (
      <DarkLoadingButton
        appearance="primary"
        onClick={() => {
          downloadInvoicesListCSV();
        }}
        testId="export-invoices-as-csv"
        isLoading={invoicesListLoading}
        iconBefore={<ExportIcon label="Export icon" size="small" />}
      >
        Export as CSV
      </DarkLoadingButton>
    );
  };

  const renderAppPage = () => {
    const columns = [
      InvoiceTableColumns.INVOICE_NUMBER,
      InvoiceTableColumns.STATUS,
      InvoiceTableColumns.AMOUNT,
      InvoiceTableColumns.ISSUED,
      InvoiceTableColumns.DUE,
      InvoiceTableColumns.CUSTOMER,
      InvoiceTableColumns.PO_NUMBER,
    ];
    return (
      <AppPage className="app-page-v2 invoice-details-page">
        <ScreenEventAnalytics
          event={{
            name: AnalyticsEventSource.PARTNER_INVOICES_SCREEN,
            attributes: {},
          }}
        />
        <AppContent>
          <section className="ph-50">
            <InvoiceTable
              id={txaId}
              transactionAccountId={txaId}
              updateDownloadCsvOptions={setDownloadCsvOptions}
              columns={columns}
              {...(isDesignIteration1Enabled && {
                shouldHideCustomiseView: true,
                showDownloadCSVButton: true,
                downloadInvoicesListCSV: downloadInvoicesListCSV,
                invoicesCSVListLoading: invoicesListLoading,
                selectedInterfaceKey: selectedTabKey,
              })}
              isOrgSearchEnabled
              isCreditNoteIconFunctional={true}
            />
          </section>
        </AppContent>
      </AppPage>
    );
  };

  if (isDesignIteration1Enabled) {
    const breadcrumbsItems = [
      {
        title: 'Home',
        href: routePaths.home,
      },
      {
        title: 'Invoices',
        href: routePaths.invoices,
      },
    ];

    const renderButtonTab = (tabName: string, tabNameKey: string) => {
      return (
        <Button
          className={classNames('invoice-button-tab', {
            'invoice-button-tab-active': selectedTabKey === tabNameKey,
          })}
          onClick={() => {
            history.replace({
              ...location,
              hash: kebabCase(tabNameKey),
            });
            setSelectedTabKey(tabNameKey);
          }}
          appearance={selectedTabKey === tabNameKey ? 'primary' : 'default'}
        >
          {tabName}
        </Button>
      );
    };

    const renderToggleActions = () => {
      return (
        <ButtonTabContainer>
          {renderButtonTab(openAndPaidTabName, openAndPaidTabNameKey)}
          {renderButtonTab(refundedTabName, refundedTabNameKey)}
        </ButtonTabContainer>
      );
    };

    return (
      <PageBackgroundV2
        heading={
          <>
            {INVOICES.HEADING} {renderToggleActions()}
          </>
        }
        breadcrumbsProps={{ items: breadcrumbsItems }}
      >
        {renderAppPage()}
      </PageBackgroundV2>
    );
  }

  return (
    <PageBackground
      heading={INVOICES.HEADING}
      imageKey={Background.INVOICE_BACKGROUND}
      className="page-background invoices-v2"
      actions={renderActions()}
    >
      {renderAppPage()}
    </PageBackground>
  );
};
